import { pojemnoscZestawuKwh } from './podsumowanieZestawu'
/**
 * Rynek zleceń: ogłoszenie z zestawu asystenta i z raportu „Sprawdź ofertę”, szkic na czas rejestracji.
 * Klucze `opis` jak opis kontroferty w backendzie (`installers/notify.POLA_OPISU`), żeby maile i panel je znały.
 */
import { znakKonta } from './teczka/konto'

const KLUCZ_SZKICU = 'vv:rynek-szkic'

const bezPustych = (o) => Object.fromEntries(
  Object.entries(o).filter(([, v]) => v !== null && v !== undefined && v !== ''),
)

/** Kod ogłoszenia do adresów (`/rynek/{kod}`, `/rynek/moje/{kod}`) zamiast kolejnego id, które zdradzało, ile ogłoszeń
 *  mamy (2026-10-22). Liczbowe id tylko awaryjnie, gdy odpowiedź nie ma kodu; backend przyjmuje oba. */
export const kodOgloszenia = (o) => o?.kod || o?.id

/** Ogłoszenie z zestawu po potwierdzeniu (`PotwierdzenieZestawu`: pozycje po podmianach, podsumowanie po zmianach). */
export const ogloszenieZZestawu = (zestaw) => {
  const poz = (k) => (zestaw?.items || []).find((p) => p.category === k)
  const panele = poz('panels')
  const falownik = poz('inverter')
  const magazyn = poz('battery')
  const s = zestaw?.summary || {}
  const opis = bezPustych({
    kwp: panele ? (s.panels_total_kwp ?? null) : null,
    liczba_modulow: panele?.quantity || null,
    moduly: panele?.model_name || null,
    falownik: falownik?.model_name || null,
    magazyn: magazyn ? `${(magazyn.quantity || 1) > 1 ? `${magazyn.quantity} × ` : ''}${magazyn.model_name}` : null,
    magazyn_kwh: magazyn ? pojemnoscZestawuKwh(s, zestaw?.items) : null,
    // Modele z bazy (uwagi przeglądu G5): wzór dla zamienników i karty modeli u wykonawcy (`przetarg.zestaw_zlecenia`).
    moduly_model_id: panele?.model_id ?? null,
    falownik_model_id: falownik?.model_id ?? null,
    magazyn_model_id: magazyn?.model_id ?? null,
    // Moc falownika do kafelka (uwagi przeglądu H7): jak moc modułów i pojemność magazynu, zamiast samej nazwy.
    falownik_kw: falownik?.spec?.power_kw ?? null,
  })
  const typ = magazyn ? (panele ? 'pv_magazyn' : 'magazyn') : 'pv'
  return { zrodlo: 'zestaw', tryb: 'szczegolowe', typ, opis }
}

/**
 * Preferencje klienta dla elementu bez wskazanego modelu (uwagi przeglądu H6): producent albo zawężenie z list
 * (`<rodzaj>_preferencja`, `<rodzaj>_marka`) i kilka słów (`<rodzaj>_uwagi`), np. „Huawei, hybrydowy”. Przy modelu
 * (`opis.<rodzaj>`, wzór dla zamienników) zostają same słowa klienta. Null, gdy nic nie ma.
 */
export const preferencjaKlienta = (opis, rodzaj) => {
  const o = opis || {}
  const czesci = [o[rodzaj] ? null : (o[`${rodzaj}_preferencja`] || o[`${rodzaj}_marka`]), o[`${rodzaj}_uwagi`]]
    .map((x) => String(x || '').trim()).filter(Boolean)
  return czesci.length ? czesci.join(', ') : null
}

/** Ogłoszenie z raportu „Sprawdź ofertę”: opis jak dla kontroferty i cena oferty brutto (firmy widzą ją za zgodą). */
export const ogloszenieZOferty = (opisOferty, ofertaNetto, vat) => {
  const opis = bezPustych(opisOferty || {})
  const zMagazynem = opis.magazyn && opis.magazyn !== 'bez magazynu'
  const typ = opis.kwp ? (zMagazynem ? 'pv_magazyn' : 'pv') : 'magazyn'
  const cena = ofertaNetto ? Math.round(ofertaNetto * (1 + (Number(vat) || 0) / 100)) : null
  return { zrodlo: 'sprawdz_oferte', tryb: 'szczegolowe', typ, opis, cena_oferty: cena }
}

/**
 * Szkic ogłoszenia przeżywa rejestrację i potwierdzenie e-maila (bramka odsyła na /rynek, `lib/powrotPoRejestracji`).
 * Pamięć przeglądarki, a nie karty (2026-10-23, makieta „VoltValue dla domu”, D8): link z maila zwykle otwiera NOWĄ
 * kartę, a pamięć karty jej nie widzi, więc zestaw z asystenta przepadał. Ważność jak link z maila: 3 dni.
 */
const WAZNOSC_SZKICU_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Szkic i wybór doboru należą do konta, które je zapisało (2026-10-25, uwagi przeglądu D12: szkic klienta otwierał
 * formularz kontu firmowemu w tej samej przeglądarce). `konto` to znak konta (`kontoSzkicu`), null u gościa.
 * Szkic gościa przechodzi na konto po zalogowaniu (tak działa rejestracja z bramki); szkic jednego konta nie
 * pokazuje się innemu ani gościowi po wylogowaniu.
 */
export const kontoSzkicu = (isAuthenticated, profil) => (isAuthenticated && profil?.email ? znakKonta(profil.email) : null)
export const szkicDlaKonta = (zapis, konto) => !zapis?.konto || zapis.konto === konto

export const zapiszSzkic = (dane, teraz = Date.now(), konto = null) => {
  try { window.localStorage.setItem(KLUCZ_SZKICU, JSON.stringify({ dane, czas: teraz, konto })) } catch { /* bez pamięci przeglądarki */ }
}
export const odczytajSzkic = (teraz = Date.now(), konto = null) => {
  try {
    const zapis = JSON.parse(window.localStorage.getItem(KLUCZ_SZKICU) || 'null')
    if (zapis?.dane && teraz - zapis.czas < WAZNOSC_SZKICU_MS) return szkicDlaKonta(zapis, konto) ? zapis.dane : null
    if (zapis) window.localStorage.removeItem(KLUCZ_SZKICU)
  } catch { /* bez pamięci przeglądarki */ }
  // Szkic zapisany przed zmianą w pamięci karty: karta otwarta w trakcie wdrożenia go nie zgubi.
  try { return JSON.parse(window.sessionStorage.getItem(KLUCZ_SZKICU) || 'null') } catch { return null }
}
export const wyczyscSzkic = () => {
  try { window.localStorage.removeItem(KLUCZ_SZKICU) } catch { /* bez pamięci przeglądarki */ }
  try { window.sessionStorage.removeItem(KLUCZ_SZKICU) } catch { /* bez pamięci karty */ }
}

/**
 * Wynik `GET /api/v1/compatibility/check-models` słowami Kowalskiego, przy wyborze sprzętu w ogłoszeniu (D4).
 * `ton`: ok, uwaga, blad, brak. Null, gdy wyniku nie ma (model spoza macierzy, błąd sieci): wtedy nic nie mówimy.
 */
export const opisZgodnosci = (wynik) => {
  if (!wynik?.user_status) return null
  const warunek = (wynik.conditions || []).find((w) => w?.value_pl)
  switch (wynik.user_status) {
    case 'compatible':
      return { ton: 'ok', tytul: 'Falownik i magazyn pasują do siebie', opis: 'Według dokumentacji producenta.' }
    case 'conditional':
      return { ton: 'uwaga', tytul: 'Pasują pod warunkami producenta',
        opis: warunek ? `${warunek.label_pl ? `${warunek.label_pl}: ` : ''}${warunek.value_pl}` : 'Firma sprawdzi je przed ofertą.' }
    case 'incompatible':
      return { ton: 'blad', tytul: 'Ten magazyn nie współpracuje z tym falownikiem',
        opis: 'Tak podaje dokumentacja producenta. Zmień jeden z nich albo zapytaj firmy w komentarzu.' }
    case 'conflicting':
      return { ton: 'uwaga', tytul: 'Źródła podają sprzeczne informacje o tej parze', opis: 'Firma potwierdzi zgodność przed ofertą.' }
    default:
      return { ton: 'brak', tytul: 'Nie mamy potwierdzonych danych o tej parze', opis: 'Firma potwierdzi zgodność przed ofertą.' }
  }
}

/**
 * Ile zostało do terminu oferty (prośba właściciela 2026-11-02): napis i kolor kropki. Ponad 24 h zielona, ponad 12 h
 * żółta, potem czerwona; dzwonek dostaje powiadomienie przy tym samym progu 12 h (backend `PRZYPOMNIENIE_PRZED_H`).
 */
export const czasDoTerminu = (iso, teraz = Date.now()) => {
  const minut = Math.floor((new Date(iso).getTime() - teraz) / 60000)
  if (!Number.isFinite(minut)) return null
  if (minut <= 0) return { tekst: 'termin minął', kolor: 'bg-gray-400' }
  const godzin = Math.floor(minut / 60)
  const kolor = minut > 24 * 60 ? 'bg-emerald-500' : minut > 12 * 60 ? 'bg-amber-400' : 'bg-red-500'
  if (godzin >= 12) return { tekst: `zostało ${godzin} godz.`, kolor }
  if (godzin >= 1) return { tekst: `zostało ${godzin} godz.${minut % 60 ? ` ${minut % 60} min` : ''}`, kolor }
  return { tekst: `zostało ${minut} min`, kolor }
}

/** „12 400 zł” */
export const zl = (v) => (v == null ? null : `${Math.round(v).toLocaleString('pl-PL')} zł`)

/** Etykiety opisu instalacji (te same klucze co w backendzie). */
export const POLA_OPISU = [
  // Zakres z „Wiem, czego chcę” (2026-10-30): które części obejmuje zlecenie, np. „Falownik, Magazyn energii”.
  ['zakres', 'Zakres', (v) => v],
  ['kwp', 'Moc', (v) => `${String(v).replace('.', ',')} kWp`],
  ['liczba_modulow', 'Liczba modułów', (v) => String(v)],
  ['moduly', 'Moduły', (v) => v],
  ['falownik', 'Falownik', (v) => v],
  ['magazyn', 'Magazyn', (v) => v],
  ['magazyn_kwh', 'Pojemność magazynu', (v) => `${String(v).replace('.', ',')} kWh`],
  ['budynek', 'Budynek', (v) => v],
  ['dach', 'Dach', (v) => v],
  // Konstrukcja z „Konkretny sprzęt” w „Wiem, czego chcę” (2026-10-30); `dach` niesie wtedy jej kategorię.
  ['konstrukcja', 'Konstrukcja', (v) => v],
  // Rozbudowa z prostego doboru (2026-10-26).
  ['rozbudowa', 'Zakres rozbudowy', (v) => v],
  ['obecna_instalacja', 'Obecna instalacja', (v) => v],
]

export const wierszeOpisu = (opis) => POLA_OPISU
  .filter(([k]) => opis?.[k] !== undefined && opis?.[k] !== null && opis?.[k] !== '')
  .map(([k, etykieta, fmt]) => [etykieta, fmt(opis[k])])

/** Opis wyboru z `WyborSprzetu` (jak w „Sprawdź ofertę”): etykieta modelu albo producent, seria i moc. */
export const opisWyboru = (w, jednostka) => {
  if (!w) return null
  if (w.model) return w.model.etykieta
  return [w.producent, w.seria, w.moc ? `${String(w.moc).replace('.', ',')} ${jednostka}` : null].filter(Boolean).join(' · ')
    || null
}
